/**
 * NF Reviews Slider
 * Custom review slider for Shopify product pages with JudgeMe integration
 */

class NFReviewsSlider {
  constructor(container) {
    this.container = container;
    this.slider = container.querySelector('.nf-reviews-slider');
    this.track = container.querySelector('.nf-reviews-track');
    this.cards = container.querySelectorAll('.nf-review-card');
    this.prevBtn = container.querySelector('.nf-reviews-prev');
    this.nextBtn = container.querySelector('.nf-reviews-next');
    this.dotsContainer = container.querySelector('.nf-reviews-dots');

    this.currentIndex = 0;
    this.cardsPerView = this.getCardsPerView();
    this.totalSlides = 0;
    this.isTransitioning = false;
    this.isInfinite = false;
    this.realCardCount = 0;
    this.transitionDuration = 400; // Must match CSS transition duration

    this.autoplay = this.slider?.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(this.slider?.dataset.autoplaySpeed || 5) * 1000;
    this.autoplayInterval = null;

    this.touchStartX = 0;
    this.touchEndX = 0;

    // JudgeMe settings
    this.judgemeEnabled = this.container.dataset.judgemeEnabled === 'true';
    this.judgemeToken = this.container.dataset.judgemeApiToken;
    this.shopDomain = this.container.dataset.shopDomain;
    this.productId = this.container.dataset.productId;
    this.judgemePerPage = parseInt(this.container.dataset.judgemePerPage) || 20;
    this.judgemeMinRating = parseInt(this.container.dataset.judgemeMinRating) || 4;
    this.judgemeScope = this.container.dataset.judgemeScope || 'shop';
    this.verifiedText = this.container.dataset.verifiedText || 'Verified Buyer';

    this.init();
  }

  async init() {
    // Fetch JudgeMe reviews if enabled
    if (this.judgemeEnabled && this.judgemeToken) {
      await this.fetchJudgemeReviews();
    }

    // Update cards reference after potential JudgeMe additions
    this.cards = this.container.querySelectorAll('.nf-review-card');

    if (this.cards.length > 0) {
      this.totalSlides = Math.ceil(this.cards.length / this.cardsPerView);

      // Setup infinite scroll if more than 1 slide
      if (this.totalSlides > 1) {
        this.setupInfiniteScroll();
      }

      this.createDots();
      this.bindEvents();
      this.updateSlider(false);

      if (this.autoplay && this.totalSlides > 1) {
        this.startAutoplay();
      }

      // Create image modal
      this.createImageModal();
    }
  }

  setupInfiniteScroll() {
    if (!this.track || this.cards.length < 2) return;

    this.isInfinite = true;
    this.realCardCount = this.cards.length;

    // Clone first and last cards
    const firstCard = this.cards[0];
    const lastCard = this.cards[this.cards.length - 1];

    const firstClone = firstCard.cloneNode(true);
    const lastClone = lastCard.cloneNode(true);

    firstClone.classList.add('nf-review-card--clone');
    firstClone.setAttribute('aria-hidden', 'true');
    lastClone.classList.add('nf-review-card--clone');
    lastClone.setAttribute('aria-hidden', 'true');

    // Append first clone to end, prepend last clone to beginning
    this.track.appendChild(firstClone);
    this.track.insertBefore(lastClone, this.track.firstChild);

    // Update cards reference to include clones
    this.allCards = this.track.querySelectorAll('.nf-review-card');

    // Start at index 1 (the real first card, after the prepended clone)
    this.currentIndex = 1;

    // Set initial position without animation
    this.updateSlider(false);
  }

  async fetchJudgemeReviews() {
    try {
      // Build the API URL
      let apiUrl = `https://judge.me/api/v1/reviews?api_token=${this.judgemeToken}&shop_domain=${this.shopDomain}&per_page=${this.judgemePerPage}`;

      // Add product filter if scope is product and we have a product ID
      if (this.judgemeScope === 'product' && this.productId) {
        apiUrl += `&product_id=${this.productId}`;
      }
      // 'all' scope fetches all product reviews (no product_id filter)
      // 'shop' scope also fetches all reviews but may include non-product reviews

      const response = await fetch(apiUrl);

      if (!response.ok) {
        console.warn('[NF Reviews] Failed to fetch JudgeMe reviews:', response.status);
        return;
      }

      const data = await response.json();

      if (data.reviews && data.reviews.length > 0) {
        // Filter by minimum rating
        let filteredReviews = data.reviews.filter(review => review.rating >= this.judgemeMinRating);

        // For 'all' scope, only include reviews that have a product associated
        if (this.judgemeScope === 'all') {
          filteredReviews = filteredReviews.filter(review => review.product_id || review.product_title);
        }

        this.renderJudgemeReviews(filteredReviews);
      }
    } catch (error) {
      console.warn('[NF Reviews] Error fetching JudgeMe reviews:', error);
    }
  }

  renderJudgemeReviews(reviews) {
    if (!this.track || reviews.length === 0) return;

    reviews.forEach(review => {
      const card = this.createReviewCard(review);
      this.track.appendChild(card);
    });
  }

  createReviewCard(review) {
    const card = document.createElement('div');
    card.classList.add('nf-review-card', 'nf-review-card--judgeme');

    // Format date
    const reviewDate = this.formatDate(review.created_at);

    // Get reviewer name (use first name + initial or anonymous)
    const reviewerName = review.reviewer?.name || 'Anonymous';

    // Build star rating HTML
    let starsHtml = '';
    for (let i = 1; i <= 5; i++) {
      if (i <= review.rating) {
        starsHtml += `<svg class="nf-star nf-star-filled" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
        </svg>`;
      } else {
        starsHtml += `<svg class="nf-star nf-star-empty" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
        </svg>`;
      }
    }

    // Build verified badge if applicable
    const verifiedBadge = review.verified === 'buyer' ? `
      <span class="nf-review-verified">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
        </svg>
        ${this.verifiedText}
      </span>
    ` : '';

    // Build title if exists
    const titleHtml = review.title ? `<h3 class="nf-review-title">${this.escapeHtml(review.title)}</h3>` : '';

    // Build images if exist
    let imagesHtml = '';
    if (review.pictures && review.pictures.length > 0) {
      const firstImage = review.pictures[0];
      const imageUrl = firstImage.urls?.huge || firstImage.urls?.original || firstImage.url;
      if (imageUrl) {
        imagesHtml = `
          <div class="nf-review-image">
            <img src="${imageUrl}" alt="Review image from ${this.escapeHtml(reviewerName)}" loading="lazy">
          </div>
        `;
      }
    }

    // Get first letter for avatar placeholder
    const avatarLetter = reviewerName.charAt(0).toUpperCase();

    // Build product name if available
    const productHtml = review.product_title ? `<span class="nf-review-product">${this.escapeHtml(review.product_title)}</span>` : '';

    card.innerHTML = `
      <div class="nf-review-header">
        <div class="nf-review-rating">${starsHtml}</div>
        <div class="nf-review-reviewer">${this.escapeHtml(reviewerName)}</div>
      </div>
      <div class="nf-review-content-wrapper">
        ${imagesHtml}
        <div class="nf-review-content">
          <div class="nf-review-body">
            <p>${this.escapeHtml(review.body)}</p>
          </div>
        </div>
      </div>
    `;

    return card;
  }

  formatDate(dateString) {
    if (!dateString) return '';

    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} month${Math.floor(diffDays / 30) > 1 ? 's' : ''} ago`;

    return date.toLocaleDateString();
  }

  escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  getCardsPerView() {
    // Always show 1 card at a time
    return 1;
  }

  createDots() {
    if (!this.dotsContainer || this.totalSlides <= 1) return;

    this.dotsContainer.innerHTML = '';
    for (let i = 0; i < this.totalSlides; i++) {
      const dot = document.createElement('button');
      dot.classList.add('nf-reviews-dot');
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      if (i === 0) dot.classList.add('active');
      dot.addEventListener('click', () => this.goToSlide(i + 1)); // +1 because of clone
      this.dotsContainer.appendChild(dot);
    }

    this.dots = this.dotsContainer.querySelectorAll('.nf-reviews-dot');
  }

  bindEvents() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prev());
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.next());
    }

    // Touch events for swipe
    if (this.track) {
      this.track.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: true });
      this.track.addEventListener('touchend', (e) => this.handleTouchEnd(e), { passive: true });
    }

    // Pause autoplay on hover
    this.container.addEventListener('mouseenter', () => this.stopAutoplay());
    this.container.addEventListener('mouseleave', () => {
      if (this.autoplay) this.startAutoplay();
    });

    // Handle resize
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => this.handleResize(), 200);
    });

    // Image click for modal
    this.container.querySelectorAll('.nf-review-image img').forEach(img => {
      img.addEventListener('click', (e) => this.openImageModal(e.target.src));
    });
  }

  handleTouchStart(e) {
    this.touchStartX = e.changedTouches[0].screenX;
  }

  handleTouchEnd(e) {
    this.touchEndX = e.changedTouches[0].screenX;
    this.handleSwipe();
  }

  handleSwipe() {
    const swipeThreshold = 50;
    const diff = this.touchStartX - this.touchEndX;

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        this.next();
      } else {
        this.prev();
      }
    }
  }

  handleResize() {
    const newCardsPerView = this.getCardsPerView();
    if (newCardsPerView !== this.cardsPerView) {
      this.cardsPerView = newCardsPerView;
      this.totalSlides = Math.ceil(this.cards.length / this.cardsPerView);
      this.currentIndex = Math.min(this.currentIndex, this.totalSlides);
      this.createDots();
      this.updateSlider(false);
    }
  }

  prev() {
    if (this.isTransitioning) return;
    this.isTransitioning = true;
    this.currentIndex--;

    // Animate to the new position
    this.track.style.transition = `transform ${this.transitionDuration}ms ease`;
    this.setTrackPosition();

    // After animation completes, check if we need to loop
    setTimeout(() => {
      this.checkInfiniteLoop();
    }, this.transitionDuration + 20);
  }

  next() {
    if (this.isTransitioning) return;
    this.isTransitioning = true;
    this.currentIndex++;

    // Animate to the new position
    this.track.style.transition = `transform ${this.transitionDuration}ms ease`;
    this.setTrackPosition();

    // After animation completes, check if we need to loop
    setTimeout(() => {
      this.checkInfiniteLoop();
    }, this.transitionDuration + 20);
  }

  checkInfiniteLoop() {
    if (!this.isInfinite) {
      this.isTransitioning = false;
      return;
    }

    // At clone of last card (index 0) - instantly jump to real last card
    if (this.currentIndex === 0) {
      this.currentIndex = this.realCardCount;
      this.instantJump();
    }
    // At clone of first card (index realCardCount + 1) - instantly jump to real first card
    else if (this.currentIndex === this.realCardCount + 1) {
      this.currentIndex = 1;
      this.instantJump();
    }
    else {
      this.isTransitioning = false;
    }
  }

  instantJump() {
    // Disable transition for instant jump
    this.track.style.transition = 'none';
    this.setTrackPosition();

    // Force browser to process the style change
    void this.track.offsetWidth;

    // Allow next navigation
    this.isTransitioning = false;
  }

  setTrackPosition() {
    const cardsToUse = this.allCards || this.cards;
    const card = cardsToUse[0];
    if (!card) return;

    const cardStyle = getComputedStyle(card);
    const cardWidth = card.offsetWidth;
    const marginLeft = parseInt(cardStyle.marginLeft) || 0;
    const marginRight = parseInt(cardStyle.marginRight) || 0;
    const slideWidth = cardWidth + marginLeft + marginRight;
    const translateX = -this.currentIndex * slideWidth;

    this.track.style.transform = `translateX(${translateX}px)`;
  }

  goToSlide(index) {
    if (this.isTransitioning) return;
    this.currentIndex = index;
    this.updateSlider(true);
  }

  updateSlider(animate = true) {
    if (!this.track) return;

    // Toggle animation
    if (animate) {
      this.track.style.transition = `transform ${this.transitionDuration}ms ease`;
    } else {
      this.track.style.transition = 'none';
    }

    this.setTrackPosition();

    // Update dots (adjust for clone offset)
    if (this.dots && this.totalSlides > 1) {
      let dotIndex = this.currentIndex - 1; // -1 because index 1 = first real slide
      if (dotIndex < 0) dotIndex = this.totalSlides - 1;
      if (dotIndex >= this.totalSlides) dotIndex = 0;

      this.dots.forEach((dot, index) => {
        dot.classList.toggle('active', index === dotIndex);
      });
    }

    // Reset transitioning flag if no animation
    if (!animate) {
      this.isTransitioning = false;
    }
  }

  startAutoplay() {
    if (this.autoplayInterval) return;

    this.autoplayInterval = setInterval(() => {
      this.next();
    }, this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  createImageModal() {
    // Check if modal already exists
    if (document.querySelector('.nf-review-modal')) return;

    const modal = document.createElement('div');
    modal.classList.add('nf-review-modal');
    modal.innerHTML = `
      <button class="nf-review-modal-close" aria-label="Close">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
      <img src="" alt="Review image">
    `;
    document.body.appendChild(modal);

    this.modal = modal;
    this.modalImg = modal.querySelector('img');
    this.modalClose = modal.querySelector('.nf-review-modal-close');

    // Close events
    this.modalClose.addEventListener('click', () => this.closeImageModal());
    modal.addEventListener('click', (e) => {
      if (e.target === modal) this.closeImageModal();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeImageModal();
    });
  }

  openImageModal(src) {
    if (!this.modal || !this.modalImg) return;
    this.modalImg.src = src;
    this.modal.classList.add('active');
    document.body.style.overflow = 'hidden';
  }

  closeImageModal() {
    if (!this.modal) return;
    this.modal.classList.remove('active');
    document.body.style.overflow = '';
  }
}

// Initialize all review sliders on the page
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.nf-reviews-section').forEach(section => {
    new NFReviewsSlider(section);
  });
});

// Re-initialize on section reload in theme editor
if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.nf-reviews-section');
    if (section) {
      new NFReviewsSlider(section);
    }
  });
}
